import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API, errorText, formatINR } from "@/lib/payments";

const emptyItem = { description: "", quantity: 1, unit_price_inr: "" };
const emptyForm = { customer_name: "", customer_email: "", customer_phone: "", company_name: "", notes: "", items: [{ ...emptyItem }] };

const ItemRow = ({ item, index, onChange, onRemove, canRemove }) => (
  <div className="grid grid-cols-12 gap-2 items-end" data-testid={`quotation-item-row-${index}`}>
    <Input className="col-span-6" placeholder="Item description" value={item.description}
      onChange={(e) => onChange(index, "description", e.target.value)} data-testid={`quotation-item-description-${index}`} />
    <Input className="col-span-2" type="number" min="1" placeholder="Qty" value={item.quantity}
      onChange={(e) => onChange(index, "quantity", e.target.value)} data-testid={`quotation-item-quantity-${index}`} />
    <Input className="col-span-3" type="number" min="0" step="0.01" placeholder="Unit ₹" value={item.unit_price_inr}
      onChange={(e) => onChange(index, "unit_price_inr", e.target.value)} data-testid={`quotation-item-price-${index}`} />
    <Button type="button" variant="ghost" size="icon" className="col-span-1" disabled={!canRemove}
      onClick={() => onRemove(index)} data-testid={`quotation-item-remove-${index}`}>
      <Trash2 className="h-4 w-4" />
    </Button>
  </div>
);

export const QuotationForm = ({ open, onOpenChange, authHeader, onCreated, inquiry }) => {
  const [form, setForm] = useState(() => (inquiry ? { ...emptyForm, customer_name: inquiry.contact_person,
    customer_email: inquiry.email, customer_phone: inquiry.phone || "", company_name: inquiry.company_name,
    notes: "", items: [{ ...emptyItem }] } : emptyForm));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => set("items", form.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const previewTotal = form.items.reduce((s, it) => s + Math.round((Number(it.unit_price_inr) || 0) * 100) * (Number(it.quantity) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, inquiry_id: inquiry?.id || null, customer_phone: form.customer_phone || null,
        company_name: form.company_name || null, notes: form.notes || null,
        items: form.items.map((it) => ({ ...it, quantity: Number(it.quantity) })) };
      const res = await axios.post(`${API}/admin/quotations`, payload, { headers: authHeader() });
      toast.success(`Draft ${res.data.number} created`);
      setForm(emptyForm);
      onCreated(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errorText(err, "Could not create quotation"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quotation-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">New quotation (draft){inquiry && ` for ${inquiry.company_name}`}</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            {[["customer_name", "Customer name *"], ["customer_email", "Customer email *"], ["company_name", "Company"], ["customer_phone", "Phone"]].map(([k, l]) => (
              <div key={k} className="space-y-1">
                <Label htmlFor={`q-${k}`}>{l}</Label>
                <Input id={`q-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)}
                  required={l.endsWith("*")} type={k === "customer_email" ? "email" : "text"} data-testid={`quotation-${k.replace("_", "-")}-input`} />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <Label>Line items (INR)</Label>
            {form.items.map((it, i) => (
              <ItemRow key={i} item={it} index={i} onChange={setItem} canRemove={form.items.length > 1}
                onRemove={(idx) => set("items", form.items.filter((_, j) => j !== idx))} />
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => set("items", [...form.items, { ...emptyItem }])} data-testid="quotation-add-item-btn">
              <Plus className="h-4 w-4 mr-1" /> Add item
            </Button>
          </div>
          <div className="space-y-1">
            <Label htmlFor="q-notes">Notes (shown to customer)</Label>
            <Textarea id="q-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="quotation-notes-input" />
          </div>
          <DialogFooter className="items-center sm:justify-between">
            <p className="text-sm text-stone-600" data-testid="quotation-preview-total">Estimated total: <b>{formatINR(previewTotal)}</b> <span className="text-xs text-stone-400">(final amount is calculated by the server)</span></p>
            <Button type="submit" disabled={saving} data-testid="quotation-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save draft
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
